import { useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import {
	IconArrowDownOutline18 as ArrowDown,
	IconArrowUpOutline18 as ArrowUp,
	IconChartTrendUpOutline18 as ChartTrendUp,
	IconCheckOutline18 as Check,
	IconDotsOutline18 as Dots,
	IconFileContentOutline18 as FileIcon,
	IconFlag3Outline18 as Flag,
	IconFolderOutline18 as FolderIcon,
	IconPenOutline18 as PenIcon,
	IconPlusOutline18 as Plus,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"

import type { HelpCenterHomeSection } from "@productlane/lib/src/help-center-home-sections"
import {
	MAX_CUSTOM_HOME_SECTIONS,
	PICKED_SECTION_ARTICLE_LIMIT,
} from "@productlane/lib/src/help-center-home-sections"
import { Button, Dialog, IconButton, Input } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
} from "@productlane/ui/src/components/internal/dropdown"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { CustomHomeSectionView } from "./home-sections"
import type { ArticleWithSubpages, GroupWithArticles } from "./types"
import { NavSectionHeader } from "@/components/shared/nav-section-header"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { generateId } from "@/lib/id"
import { mutateOk, useZero } from "@/lib/zero"
import { useActivePortalInstanceStore } from "@/stores/active-portal-instance-store"
import { HelpCenterHomeTile } from "./help-center-home-tile"
import { AddArticleSlot, RemoveFromHomeAction } from "./home-curated-section"
import {
	customHomeSectionViews,
	HOME_SECTION_LABEL,
	pickableHomeFolders,
} from "./home-sections"

function useRunMutation() {
	const zero = useZero()
	return (mutation: Parameters<typeof zero.mutate>[0]) =>
		mutateOk(zero.mutate(mutation)).catch((error: unknown) => {
			toast.error({
				title: error instanceof Error ? error.message : "Something went wrong",
			})
		})
}

function SectionNameDialog({
	title,
	submitLabel,
	initialName,
	allowEmpty,
	isOpen,
	onClose,
	onSubmit,
}: {
	title: string
	submitLabel: string
	initialName: string
	/** A folder section falls back to the folder's name. */
	allowEmpty: boolean
	isOpen: boolean
	onClose: () => void
	onSubmit: (name: string) => void
}) {
	const [name, setName] = useState(initialName)
	const canSubmit = allowEmpty || name.trim().length > 0

	return (
		<Dialog
			className="max-w-80 p-2"
			isOpen={isOpen}
			onOpenChange={(open) => {
				if (!open) onClose()
				else setName(initialName)
			}}
		>
			<h2 className="mx-2 mb-4 mt-1 text-lg font-bold">{title}</h2>
			<form
				className="space-y-2 px-2 py-2"
				onSubmit={(event) => {
					event.preventDefault()
					if (!canSubmit) return
					onSubmit(name.trim())
					onClose()
				}}
			>
				<Input
					name="sectionName"
					autoFocus
					value={name}
					onChange={(event) => setName(event.target.value)}
					placeholder="Section name"
					className="w-full text-sm text-primary outline-none"
				/>
				<Button
					type="submit"
					variant="outline"
					disabled={!canSubmit}
					className="w-full rounded-md border p-2"
				>
					{submitLabel}
				</Button>
			</form>
		</Dialog>
	)
}

function FolderMenuItems({
	folders,
	currentGroupId,
	onPick,
}: {
	folders: readonly GroupWithArticles[]
	currentGroupId?: string | null
	onPick: (groupId: string) => void
}) {
	return (
		<>
			{folders.map((folder) => (
				<DropdownMenuItem
					key={folder.id}
					Icon={currentGroupId === folder.id ? Check : undefined}
					disabled={currentGroupId === folder.id}
					onClick={() => onPick(folder.id)}
				>
					<span className="truncate">{folder.name}</span>
				</DropdownMenuItem>
			))}
		</>
	)
}

function HomeCustomSection({
	view,
	freeFolders,
	candidates,
	canMoveUp,
	canMoveDown,
}: {
	view: CustomHomeSectionView
	freeFolders: readonly GroupWithArticles[]
	candidates: readonly ArticleWithSubpages[]
	canMoveUp: boolean
	canMoveDown: boolean
}) {
	const run = useRunMutation()
	const [isRenameOpen, setIsRenameOpen] = useState(false)
	const { section, group, title, articles } = view
	const isFolder = Boolean(section.groupId)
	const move = (direction: "up" | "down") =>
		void run(
			mutators.helpCenterCustomHomeSections.move({ id: section.id, direction }),
		)
	return (
		<div className="mb-12">
			<NavSectionHeader
				label={title}
				className="-ml-2 mb-3"
				action={
					<>
						<RoleCheck not={["VIEWER"]}>
							<DropdownMenu
								className="w-56"
								contentProps={{ align: "end" }}
								trigger={
									<IconButton
										Icon={Dots}
										variant="ghost"
										size="md"
										iconStrokeWidth={1.75}
										iconClassName="h-3 w-3"
										aria-label="Section actions"
										className="text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700"
										type="button"
									/>
								}
							>
								<DropdownMenuItem
									Icon={PenIcon}
									onClick={() => setIsRenameOpen(true)}
								>
									Rename
								</DropdownMenuItem>
								{isFolder && freeFolders.length > 0 && (
									<DropdownMenuSub>
										<DropdownMenuSubTrigger className="gap-3 px-2 text-secondary">
											<FolderIcon className="h-4 w-4 flex-none" />
											Change folder
										</DropdownMenuSubTrigger>
										<DropdownMenuPortal>
											<DropdownMenuSubContent className="max-h-80 overflow-y-auto">
												<FolderMenuItems
													folders={freeFolders}
													currentGroupId={section.groupId}
													onPick={(groupId) =>
														void run(
															mutators.helpCenterCustomHomeSections.changeFolder(
																{
																	id: section.id,
																	groupId,
																},
															),
														)
													}
												/>
											</DropdownMenuSubContent>
										</DropdownMenuPortal>
									</DropdownMenuSub>
								)}
								{canMoveUp && (
									<DropdownMenuItem Icon={ArrowUp} onClick={() => move("up")}>
										Move up
									</DropdownMenuItem>
								)}
								{canMoveDown && (
									<DropdownMenuItem
										Icon={ArrowDown}
										onClick={() => move("down")}
									>
										Move down
									</DropdownMenuItem>
								)}
								<DropdownMenuSeparator />
								<DropdownMenuItem
									Icon={Trash}
									onClick={() =>
										void run(
											mutators.helpCenterCustomHomeSections.remove({
												id: section.id,
											}),
										)
									}
								>
									Remove from home
								</DropdownMenuItem>
							</DropdownMenu>
						</RoleCheck>
					</>
				}
			/>
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{articles.map((article) => (
					<HelpCenterHomeTile
						key={article.id}
						article={article}
						action={
							isFolder ? undefined : (
								<RemoveFromHomeAction
									onRemove={() =>
										void run(
											mutators.helpCenterCustomHomeSections.setArticle({
												articleId: article.id,
												sectionId: null,
											}),
										)
									}
								/>
							)
						}
					/>
				))}
				{!isFolder && articles.length < PICKED_SECTION_ARTICLE_LIMIT && (
					<RoleCheck not={["VIEWER"]}>
						<AddArticleSlot
							candidates={candidates}
							onPick={(articleId) =>
								void run(
									mutators.helpCenterCustomHomeSections.setArticle({
										articleId,
										sectionId: section.id,
									}),
								)
							}
						/>
					</RoleCheck>
				)}
			</div>
			<SectionNameDialog
				title="Rename section"
				submitLabel="Rename"
				initialName={section.name ?? group?.name ?? ""}
				allowEmpty={isFolder}
				isOpen={isRenameOpen}
				onClose={() => setIsRenameOpen(false)}
				onSubmit={(name) =>
					void run(
						mutators.helpCenterCustomHomeSections.rename({
							id: section.id,
							name: name || null,
						}),
					)
				}
			/>
		</div>
	)
}

/** The sections a team adds after Popular, and the button that adds them. */
const CURATED_ICON = { GET_STARTED: Flag, POPULAR: ChartTrendUp }

export function HomeCustomSections({
	articleGroups,
	shownAbove,
	candidates,
	addableCurated,
	onAddCurated,
}: {
	articleGroups: readonly GroupWithArticles[]
	shownAbove: readonly ArticleWithSubpages[]
	candidates: readonly ArticleWithSubpages[]
	addableCurated: readonly HelpCenterHomeSection[]
	onAddCurated: (section: HelpCenterHomeSection) => void
}) {
	const run = useRunMutation()
	const portalInstanceId = useActivePortalInstanceStore(
		(s) => s.activePortalInstanceId,
	)
	const [sections] = useQuery(queries.helpCenterCustomHomeSections())
	const [isPickOpen, setIsPickOpen] = useState(false)

	const views = useMemo(
		() =>
			customHomeSectionViews({
				sections,
				articleGroups,
				portalInstanceId,
				shownAbove,
			}),
		[sections, articleGroups, portalInstanceId, shownAbove],
	)
	const canAddCustom = views.length < MAX_CUSTOM_HOME_SECTIONS
	const freeFolders = useMemo(
		() => pickableHomeFolders(articleGroups, views),
		[articleGroups, views],
	)

	const addFolder = (groupId: string) =>
		void run(
			mutators.helpCenterCustomHomeSections.addFolder({
				id: generateId(),
				groupId,
				portalInstanceId,
			}),
		)

	return (
		<>
			{views.map((view, index) => (
				<HomeCustomSection
					key={view.section.id}
					view={view}
					freeFolders={freeFolders}
					candidates={candidates}
					canMoveUp={index > 0}
					canMoveDown={index < views.length - 1}
				/>
			))}
			{(canAddCustom || addableCurated.length > 0) && (
				<RoleCheck not={["VIEWER"]}>
					<div className="mb-12">
						<DropdownMenu
							className="w-60"
							contentProps={{ align: "start" }}
							trigger={
								<Button variant="outline" size="sm" Icon={Plus} type="button">
									Add section
								</Button>
							}
						>
							{addableCurated.map((section) => (
								<DropdownMenuItem
									key={section}
									Icon={CURATED_ICON[section]}
									onClick={() => onAddCurated(section)}
								>
									{HOME_SECTION_LABEL[section]}
								</DropdownMenuItem>
							))}
							{addableCurated.length > 0 && canAddCustom && (
								<DropdownMenuSeparator />
							)}
							{canAddCustom && freeFolders.length > 0 && (
								<DropdownMenuSub>
									<DropdownMenuSubTrigger className="gap-3 px-2 text-secondary">
										<FolderIcon className="h-4 w-4 flex-none" />
										From a folder
									</DropdownMenuSubTrigger>
									<DropdownMenuPortal>
										<DropdownMenuSubContent className="max-h-80 overflow-y-auto">
											<FolderMenuItems
												folders={freeFolders}
												onPick={addFolder}
											/>
										</DropdownMenuSubContent>
									</DropdownMenuPortal>
								</DropdownMenuSub>
							)}
							{canAddCustom && (
								<DropdownMenuItem
									Icon={FileIcon}
									onClick={() => setIsPickOpen(true)}
								>
									Pick articles
								</DropdownMenuItem>
							)}
						</DropdownMenu>
					</div>
					<SectionNameDialog
						title="New section"
						submitLabel="Add section"
						initialName=""
						allowEmpty={false}
						isOpen={isPickOpen}
						onClose={() => setIsPickOpen(false)}
						onSubmit={(name) =>
							void run(
								mutators.helpCenterCustomHomeSections.addPicked({
									id: generateId(),
									name,
									portalInstanceId,
								}),
							)
						}
					/>
				</RoleCheck>
			)}
		</>
	)
}
